import { inject, Injectable } from '@angular/core';
import {
  ConsentCertificateParams,
  ConsentPayload,
  ConsentProofRequest,
  CustodyInfo,
} from '../../interfaces/certificate.interface';
import { CryptoService } from './crypto.service';
import { DeviceInfoService } from './device-info.service';
import { SensorDataService } from './sensor-data.service';

@Injectable({
  providedIn: 'root',
})
export class ConsentEvidenceService {
  private readonly cryptoService = inject(CryptoService);
  private readonly deviceInfoService = inject(DeviceInfoService);
  private readonly sensorDataService = inject(SensorDataService);

  /**
   * Initialize evidence collection when entering waiver signing flow
   */
  startEvidenceCollection(): void {
    this.sensorDataService.startTracking();
  }

  /**
   * Stop evidence collection (call on component destroy)
   */
  stopEvidenceCollection(): void {
    this.sensorDataService.dispose();
  }

  /**
   * Track scroll event
   */
  trackScroll(scrollOffset: number): void {
    this.sensorDataService.recordScroll(scrollOffset);
  }

  /**
   * Track tap/click event
   */
  trackTap(): void {
    this.sensorDataService.recordTap();
  }

  /**
   * Track document view
   */
  trackDocumentView(): void {
    this.sensorDataService.recordDocumentView();
  }

  /**
   * Track consent action
   */
  trackConsentClick(): void {
    this.sensorDataService.recordConsentClick();
  }

  /**
   * Generate consent certificate with cryptographic signature
   *
   * @param params - Certificate parameters including collection_id and contract_ids
   * @returns Complete ConsentProofRequest ready to submit to API
   * @throws Error if Web Crypto API is unavailable
   */
  async generateConsentCertificate(
    params: ConsentCertificateParams,
  ): Promise<ConsentProofRequest> {
    // Check crypto availability before proceeding
    if (!this.cryptoService.isWebCryptoAvailable()) {
      throw new Error(
        'Web Crypto API is not available. Please use a modern browser.',
      );
    }

    // Step 1: Collect device info
    const deviceInfo = await this.deviceInfoService.collectDeviceInfo();

    // Step 2: Get approximate location (may be null if denied/unavailable)
    const location = await this.deviceInfoService.getApproximateLocation();

    // Step 3: Collect sensor data
    const sensorData = this.sensorDataService.getSensorDataForPayload(location);

    // Step 4: Get or create cryptographic key pair
    const keyPair = await this.cryptoService.getOrCreateKeyPair();

    // Step 5: Build the payload with custody timestamps
    const now = new Date().toISOString();
    const custody: CustodyInfo = {
      created_at: now,
      signed_at: now,
      delivered_at: now, // Will be updated on actual delivery
    };

    const payload: ConsentPayload = {
      collection_id: params.collection_id,
      contracts: params.contract_ids,
      custody: custody,
      device_info: deviceInfo,
      sensor_data: sensorData,
    };

    // Step 6: Stringify and sign the payload
    const payloadJson = JSON.stringify(payload);

    const signature = await this.cryptoService.signData(
      payloadJson,
      keyPair.privateKey,
    );

    // Step 7: Extract public key components
    const publicKeyComponents = await this.cryptoService.exportPublicKeyComponents(
      keyPair.publicKey,
    );

    // Step 8: Build and return the complete request
    const request: ConsentProofRequest = {
      payload: payloadJson,
      signature: signature,
      public_key: publicKeyComponents,
    };

    return request;
  }
}

